import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Search, Plus } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { isISBNQuery, normalizeISBN } from "@/utils/isbnUtils";

interface SearchSeriesBookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  seriesName?: string;
  authorName?: string;
  position?: number;
  suggestedTitle?: string;
  suggestedIsbn?: string;
  onBookSelected?: (bookId: string, bookData: any) => void;
}

export function SearchSeriesBookDialog({
  open,
  onOpenChange,
  seriesName,
  authorName,
  position,
  suggestedTitle,
  suggestedIsbn,
  onBookSelected,
}: SearchSeriesBookDialogProps) {
  const [bookTitle, setBookTitle] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<any>(null);

  const handleSearch = async () => {
    const userInput = bookTitle.trim();
    const suggestedIsbnValue = suggestedIsbn?.trim();
    const suggestedTitleValue = suggestedTitle?.trim();
    
    // Detect if user input is an ISBN
    let searchIsbn: string | undefined;
    let searchTitle: string | undefined;
    
    if (userInput && isISBNQuery(userInput)) {
      // User typed an ISBN
      searchIsbn = normalizeISBN(userInput);
    } else if (suggestedIsbnValue) {
      // Use suggested ISBN if available
      searchIsbn = suggestedIsbnValue;
      searchTitle = userInput || suggestedTitleValue;
    } else {
      // Regular title search
      searchTitle = userInput || suggestedTitleValue;
    }
    
    if (!searchIsbn && !searchTitle) {
      toast.error("No ISBN or title available to search");
      return;
    }

    setSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke('search-and-enrich-series-book', {
        body: {
          isbn: searchIsbn,
          bookTitle: searchTitle,
          seriesName,
          authorName,
          position,
        },
      });

      if (error) throw error;

      setResults(data);
      
      if (data.existingBooks.length === 0 && data.openLibraryResults.length === 0) {
        toast.info("No results found. Try a different search.");
      }
    } catch (error: any) {
      console.error('Search error:', error);
      toast.error(`Search failed: ${error.message}`);
    } finally {
      setSearching(false);
    }
  };

  // Auto-search when dialog opens with suggested data
  useEffect(() => {
    if (open && (suggestedTitle || suggestedIsbn)) {
      setBookTitle(suggestedTitle || "");
      // Trigger search automatically only if we have ISBN or title
      const timer = setTimeout(() => {
        handleSearch();
      }, 300);
      return () => clearTimeout(timer);
    } else if (!open) {
      // Reset when dialog closes
      setBookTitle("");
      setResults(null);
    }
  }, [open]);

  const handleSelectBook = (book: any, source: 'existing' | 'openlibrary') => {
    if (source === 'existing') {
      onBookSelected?.(book.id, book);
      toast.success("Book selected from library");
      onOpenChange(false);
    } else {
      // For OpenLibrary results, we'd need to import the book first
      handleImportFromOpenLibrary(book);
    }
  };

  const handleImportFromOpenLibrary = async (book: any) => {
    try {
      // Create a new book entry
      const { data: newBook, error } = await supabase
        .from('books')
        .insert({
          title: book.title,
          authors: book.authors,
          cover_url: book.cover_url,
          isbn13: book.isbn13,
          published_year: book.publishedYear,
          source: book.source || 'openlibrary',
          series_name: seriesName,
          series_position: position,
        })
        .select()
        .single();

      if (error) throw error;

      onBookSelected?.(newBook.id, newBook);
      toast.success("Book imported and selected");
      onOpenChange(false);
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(`Failed to import book: ${error.message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>
            {suggestedTitle ? `Confirm: ${suggestedTitle}` : 'Search & Add Book to Series'}
          </DialogTitle>
          <DialogDescription>
            {seriesName && `Adding to ${seriesName}`}
            {position && ` - Position ${position}`}
            {suggestedIsbn && ` • ISBN: ${suggestedIsbn}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1 space-y-2">
              <Label htmlFor="bookTitle">Book Title or ISBN</Label>
              <Input
                id="bookTitle"
                placeholder="Enter book title or ISBN (e.g., 0-7475-5100-6)..."
                value={bookTitle}
                onChange={(e) => setBookTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={searching || !bookTitle.trim()}
              className="mt-8"
            >
              {searching ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </>
              )}
            </Button>
          </div>

          {results && (
            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
              {results.existingBooks.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">In Your Library</h3>
                  <div className="space-y-2">
                    {results.existingBooks.map((book: any) => (
                      <Card key={book.id} className="p-3 bg-card">
                        <div className="flex gap-3 items-start">
                          {book.cover_url && (
                            <img
                              src={book.cover_url}
                              alt={book.title}
                              className="w-12 h-16 object-cover rounded flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <h4 className="font-medium break-words line-clamp-2">{book.title}</h4>
                            <p className="text-sm text-muted-foreground truncate">
                              {book.authors?.join(', ')}
                            </p>
                            {book.series_name && (
                              <p className="text-xs text-muted-foreground truncate">
                                {book.series_name} #{book.series_position}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleSelectBook(book, 'existing')}
                            className="flex-shrink-0 whitespace-nowrap"
                          >
                            <Plus className="mr-1 h-3 w-3" />
                            Select
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {results.openLibraryResults.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">From Book APIs</h3>
                  <div className="space-y-2">
                    {results.openLibraryResults.map((book: any, idx: number) => (
                      <Card key={idx} className="p-3 bg-card">
                        <div className="flex gap-3 items-start">
                          {book.cover_url && (
                            <img
                              src={book.cover_url}
                              alt={book.title}
                              className="w-12 h-16 object-cover rounded flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <h4 className="font-medium break-words line-clamp-2">{book.title}</h4>
                            <p className="text-sm text-muted-foreground truncate">
                              {book.authors?.join(', ')}
                            </p>
                            {book.publishedYear && (
                              <p className="text-xs text-muted-foreground">
                                Published: {book.publishedYear}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleSelectBook(book, 'openlibrary')}
                            className="flex-shrink-0 whitespace-nowrap"
                          >
                            <Plus className="mr-1 h-3 w-3" />
                            Import
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {results.existingBooks.length === 0 && results.openLibraryResults.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No results found. Try adjusting your search.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}